import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { leadService, type PendingDiscount, type DiscountLimits } from '../services/leads'
import type { Lead, Quote, TelecallerWeight } from '../../../types'
import { UserRole, CallStatus, CallSource, TERMINAL_CALL_STATUSES } from '../../../types'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import { isNativeApp } from '../../../lib/platform'
import { telephonyService } from '../../telephony/services/telephony'
import { pollAndUpload } from '../../telephony/services/nativeRecording'
import { LEAD_STATUS_COLORS, LEAD_STATUS_LABELS } from '../../../lib/constants'
import CallDispositionModal from '../components/CallDispositionModal'
import EnquiryTrackingCard from '../components/EnquiryTrackingCard'
import PreviousPolicyCard from '../components/PreviousPolicyCard'
import WhatsAppSendModal from '../../communication/components/WhatsAppSendModal'
import MessageHistory from '../../communication/components/MessageHistory'
import IssuePolicyModal from '../../policy/components/IssuePolicyModal'
import PolicyPdfUpload from '../../policy/components/PolicyPdfUpload'
import {
  ArrowLeft, Phone, PhoneCall, MessageSquare, FileText, Info, Clock,
  Upload, Send,
  IndianRupee, CheckCircle, X, Eye, Link as LinkIcon, Download,
  AlertCircle, Percent, ChevronUp, UserCheck, BookUser,
} from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import QuoteUploadWizard from '../../quotes/components/QuoteUploadWizard'

export default function LeadDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const currentUser = useCurrentUser()
  const role = currentUser?.role as UserRole | undefined
  const canIssuePolicy = !!role && [UserRole.TELECALLER, UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL].includes(role)
  const canInitiateCall = !!role && [UserRole.TELECALLER, UserRole.TEAM_LEADER].includes(role)
  const canRevealMobile = !!role && [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.TELECALLER].includes(role)
  const canSendPaymentLink = !!role && [UserRole.TELECALLER, UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL].includes(role)
  const canViewCustomer = !!role && [UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL, UserRole.BACKEND_ADMIN].includes(role)
  const smartConnectEnabled = !!currentUser?.tenant_feature_flags?.['smart_connect_enabled']
  const logImmediately = !!currentUser?.tenant_feature_flags?.['smart_connect_log_immediately']
  const whatsappEnabled = !!currentUser?.tenant_feature_flags?.['whatsapp_enabled']

  const [lead, setLead] = useState<Lead | null>(null)
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(true)

  const [recordingMode, setRecordingMode] = useState('android_native')
  const [, setAndroidOem]                  = useState('samsung')

  const [initiating, setInitiating] = useState(false)
  const [activeCallLogId, setActiveCallLogId] = useState<number | null>(null)
  const [activeCallStatus, setActiveCallStatus] = useState<CallStatus | null>(null)
  const [activeCallSource, setActiveCallSource] = useState<CallSource>(CallSource.EXOTEL)
  const [showCallModal, setShowCallModal] = useState(false)
  const [pollTimedOut, setPollTimedOut] = useState(false)
  const [recordingFound, setRecordingFound] = useState(false)
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollStartedAtRef = useRef<number | null>(null)

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
  }

  useEffect(() => () => { stopPolling() }, [])

  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false)
  const [showPolicyModal, setShowPolicyModal] = useState(false)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const [revealedMobile, setRevealedMobile] = useState<string | null>(null)
  const [revealing, setRevealing] = useState(false)

  const [paymentUrl, setPaymentUrl] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentQrFile, setPaymentQrFile] = useState<File | null>(null)
  const [paymentQrPreview, setPaymentQrPreview] = useState<string | null>(null)
  const [sendingPayment, setSendingPayment] = useState(false)

  const [downloadingQuote, setDownloadingQuote] = useState<number | null>(null)
  const [pageNotif, setPageNotif] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const notify = (type: 'success' | 'error', message: string) => setPageNotif({ type, message })

  const [pendingDiscounts, setPendingDiscounts] = useState<PendingDiscount[]>([])
  const [discountLimits, setDiscountLimits]     = useState<DiscountLimits>({ telecaller: 0, team_leader: 15, insurance_head: 30 })
  const [discountAction, setDiscountAction] = useState<{ callId: number; mode: 'reject' | 'escalate' } | null>(null)
  const [discountComment, setDiscountComment] = useState('')
  const [discountActing, setDiscountActing] = useState(false)

  const canActOnDiscount = !!role && [UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL].includes(role)
  const canReassign = !!role && [UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL].includes(role)

  const [showReassignModal, setShowReassignModal] = useState(false)
  const [telecallers, setTelecallers]             = useState<TelecallerWeight[]>([])
  const [reassigning, setReassigning]             = useState(false)
  const [selectedTcId, setSelectedTcId]           = useState<number | null>(null)

  const openReassignModal = async () => {
    setShowReassignModal(true)
    if (telecallers.length === 0) {
      try {
        const data = await leadService.getTelecallerWeights()
        setTelecallers(data)
      } catch { /* silently ignore — table will be empty */ }
    }
  }

  const handleReassign = async () => {
    if (!lead || selectedTcId == null) return
    setReassigning(true)
    try {
      await leadService.reassignLead(lead.id, selectedTcId)
      setShowReassignModal(false)
      setSelectedTcId(null)
      notify('success', 'Lead reassigned successfully')
      await loadData()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notify('error', detail || 'Failed to reassign lead')
    } finally {
      setReassigning(false)
    }
  }

  const loadPendingDiscounts = useCallback(async () => {
    if (!canActOnDiscount) return
    const [all, lim] = await Promise.all([
      leadService.getPendingDiscounts().catch(() => []),
      leadService.getDiscountLimits().catch(() => null),
    ])
    setPendingDiscounts(all.filter(d => d.lead_id === Number(id)))
    if (lim) setDiscountLimits(lim)
  }, [canActOnDiscount, id])

  const handleDiscountApprove = async (callId: number) => {
    setDiscountActing(true)
    try {
      await leadService.approveDiscount(callId)
      notify('success', 'Discount approved.')
      loadPendingDiscounts()
    } catch { notify('error', 'Failed to approve discount.') }
    finally { setDiscountActing(false) }
  }

  const handleDiscountAction = async () => {
    if (!discountAction) return
    setDiscountActing(true)
    try {
      if (discountAction.mode === 'reject') {
        await leadService.rejectDiscount(discountAction.callId, discountComment)
        notify('success', 'Discount rejected.')
      } else {
        await leadService.escalateDiscount(discountAction.callId, discountComment)
        notify('success', 'Escalated to Insurance Head.')
      }
      setDiscountAction(null)
      setDiscountComment('')
      loadPendingDiscounts()
    } catch { notify('error', 'Action failed.') }
    finally { setDiscountActing(false) }
  }

  const [quoteSending, setQuoteSending] = useState(false)

  const loadData = async () => {
    if (!id) return
    const [leadData, quoteList] = await Promise.all([
      leadService.getById(Number(id)),
      leadService.listQuotes(Number(id)),
    ])
    setLead(leadData)
    setQuotes(quoteList)
    loadPendingDiscounts()
  }

  useEffect(() => {
    loadData().finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    telephonyService.getRecordingConfig().then(cfg => {
      setRecordingMode(cfg.call_recording_mode)
      setAndroidOem(cfg.android_oem)
    }).catch(() => { /* use defaults */ })
  }, [])

  const handleInitiateCall = async () => {
    if (!lead) return
    setInitiating(true)
    try {
      if (smartConnectEnabled) {
        // ── Exotel click-to-call ─────────────────────────────────────────
        try {
          const result = await leadService.initiateCall(lead.id)
          setActiveCallLogId(result.call_log_id)
          setActiveCallSource(CallSource.EXOTEL)
          setActiveCallStatus(CallStatus.INITIATED)
          setShowCallModal(true)
          stopPolling()
          setPollTimedOut(false)
          pollStartedAtRef.current = Date.now()
          pollRef.current = setInterval(async () => {
            const startedAt = pollStartedAtRef.current
            // If the call never reaches a terminal status within 3 minutes
            // (webhook lost, provider outage, etc.), stop polling and let the
            // telecaller log the disposition manually instead of being stuck.
            if (startedAt !== null && Date.now() - startedAt >= 3 * 60 * 1000) {
              stopPolling()
              setPollTimedOut(true)
              return
            }
            try {
              const s = await leadService.getCallStatus(result.call_log_id)
              setActiveCallStatus(s.call_status)
              if (TERMINAL_CALL_STATUSES.has(s.call_status)) stopPolling()
            } catch { /* keep polling */ }
          }, 5000)
        } catch (exotelErr: unknown) {
          // ── Fallback: native dialer on mobile, error on desktop ──────────
          if (await isNativeApp()) {
            notify('error', 'Smart Connect unavailable — opening native dialer.')
            const { mobile } = await leadService.revealMobile(lead.id)
            const { call_log_id: mobileCallLogId } = await leadService.mobileInitiateCall(lead.id)
            const callStartedAt = new Date()
            window.open(`tel:${mobile}`)
            setActiveCallLogId(mobileCallLogId)
            setActiveCallSource(CallSource.MANUAL_MOBILE)
            setActiveCallStatus(null)
            setShowCallModal(true)
            if (recordingMode === 'android_native') {
              pollAndUpload(mobileCallLogId, callStartedAt).then(pollResult => {
                setActiveCallStatus(CallStatus.COMPLETED)
                if (pollResult === 'uploaded') setRecordingFound(true)
              })
            } else {
              setActiveCallStatus(CallStatus.COMPLETED)
            }
          } else {
            const detail = (exotelErr as { response?: { data?: { detail?: string } } })?.response?.data?.detail
            notify('error', detail || 'Could not initiate call. Ensure your account has a mobile number and Exotel is configured.')
          }
        }
      } else if (await isNativeApp()) {
        // ── Native dialer (Smart Connect OFF · Mobile) ───────────────────
        const { mobile } = await leadService.revealMobile(lead.id)
        const { call_log_id: mobileCallLogId } = await leadService.mobileInitiateCall(lead.id)
        const callStartedAt = new Date()
        window.open(`tel:${mobile}`)
        setActiveCallLogId(mobileCallLogId)
        setActiveCallSource(CallSource.MANUAL_MOBILE)
        setActiveCallStatus(null)
        setShowCallModal(true)
        if (recordingMode === 'android_native') {
          pollAndUpload(mobileCallLogId, callStartedAt).then(pollResult => {
            setActiveCallStatus(CallStatus.COMPLETED)
            if (pollResult === 'uploaded') setRecordingFound(true)
          })
        } else {
          setActiveCallStatus(CallStatus.COMPLETED)
        }
      } else {
        // ── Desktop manual: reveal number, show Log Call button ──────────
        const { mobile } = await leadService.revealMobile(lead.id)
        setRevealedMobile(mobile)
        if (revealTimerRef.current) clearTimeout(revealTimerRef.current)
        revealTimerRef.current = setTimeout(() => setRevealedMobile(null), 30_000)
        if (logImmediately) {
          setActiveCallLogId(null)
          setActiveCallSource(CallSource.MANUAL_DESKTOP)
          setActiveCallStatus(null)
          setShowCallModal(true)
        }
      }
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notify('error', detail || 'Could not initiate call.')
    } finally {
      setInitiating(false)
    }
  }

  const handleLogManualCall = () => {
    setActiveCallLogId(null)
    setActiveCallSource(CallSource.MANUAL_DESKTOP)
    setActiveCallStatus(null)
    setShowCallModal(true)
  }

  const handleCallSaved = async () => {
    stopPolling()
    setShowCallModal(false)
    setActiveCallLogId(null)
    setActiveCallStatus(null)
    setPollTimedOut(false)
    setRecordingFound(false)
    await loadData()
  }

  const handleSendQuotes = async () => {
    if (!lead) return
    setQuoteSending(true)
    try {
      await leadService.sendQuotes(lead.id)
      await loadData()
      notify('success', 'Quotes sent to customer via WhatsApp')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notify('error', detail || 'Failed to send quotes')
    } finally {
      setQuoteSending(false)
    }
  }

  const handleDownloadQuote = async (quoteId: number, filename: string) => {
    setDownloadingQuote(quoteId)
    try {
      const { token } = await leadService.getQuotePdfToken(quoteId)
      const url = `/api/v1/quotes/${quoteId}/pdf?token=${encodeURIComponent(token)}`
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notify('error', detail || 'Failed to generate download link')
    } finally {
      setDownloadingQuote(null)
    }
  }

  const handleRevealMobile = async () => {
    if (!lead) return
    setRevealing(true)
    try {
      const result = await leadService.revealMobile(lead.id)
      setRevealedMobile(result.mobile)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notify('error', detail || 'Could not reveal mobile number')
    } finally {
      setRevealing(false)
    }
  }

  const handleSendPaymentLink = async () => {
    if (!lead) return
    if (!paymentUrl) { notify('error', 'Enter the payment URL'); return }
    if (!paymentAmount) { notify('error', 'Enter the amount'); return }
    if (!paymentQrFile) { notify('error', 'Upload a QR code image'); return }
    const parsed = parseFloat(paymentAmount)
    if (isNaN(parsed) || parsed <= 0) { notify('error', 'Enter a valid positive amount'); return }
    if (!/^https?:\/\/.+\..+/i.test(paymentUrl)) { notify('error', 'Enter a valid payment URL starting with http:// or https://'); return }
    setSendingPayment(true)
    try {
      await leadService.sendPaymentLink(lead.id, paymentUrl, parsed, paymentQrFile)
      setShowPaymentModal(false)
      setPaymentUrl('')
      setPaymentAmount('')
      setPaymentQrFile(null)
      setPaymentQrPreview(null)
      notify('success', 'Payment request sent to customer via WhatsApp')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notify('error', detail || 'Failed to send payment request')
    } finally {
      setSendingPayment(false)
    }
  }

  const handleQrFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    setPaymentQrFile(file)
    if (paymentQrPreview) URL.revokeObjectURL(paymentQrPreview)
    setPaymentQrPreview(file ? URL.createObjectURL(file) : null)
  }

  if (loading) return <div className="empty-state">Loading…</div>
  if (!lead) return <div className="empty-state">Lead not found</div>

  const draftQuotes = quotes.filter(q => q.status === 'DRAFT')
  const sentQuotes = quotes.filter(q => q.status !== 'DRAFT')

  return (
    <div className="space-y-6" style={{ maxWidth: '896px' }}>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm transition-colors"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header card */}
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{lead.customer_name}</h1>
            <p className="mt-1" style={{ color: 'var(--text-tertiary)' }}>{lead.mobile}</p>
            {canViewCustomer && lead.customer_id && (
              <Link
                to={`/customers/${lead.customer_id}`}
                className="inline-flex items-center gap-1 mt-1"
                style={{ fontSize: 12, color: 'var(--brand-primary)', textDecoration: 'none' }}
              >
                <BookUser className="h-3.5 w-3.5" />
                View Customer Profile
              </Link>
            )}
          </div>
          <span className={LEAD_STATUS_COLORS[lead.status] || 'badge-neutral'}>
            {LEAD_STATUS_LABELS[lead.status] ?? lead.status.replace(/_/g, ' ')}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {[
            { label: 'Vehicle Reg',    value: lead.vehicle_reg_no || '—' },
            { label: 'Model',          value: lead.vehicle_model || '—' },
            { label: 'Insurance Due',  value: lead.insurance_due_date ? new Date(lead.insurance_due_date).toLocaleDateString() : '—' },
            { label: 'Source',         value: lead.lead_source.replace(/_/g, ' ') },
            { label: 'Claim Last Year', value: lead.had_claim_last_year == null ? '—' : lead.had_claim_last_year ? 'Yes' : 'No' },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{label}</p>
              <p className="font-medium mt-0.5" style={{ color: 'var(--text-primary)' }}>{value}</p>
            </div>
          ))}
          <div>
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Priority Score</p>
            <p className="font-semibold text-lg mt-0.5" style={{ color: 'var(--text-primary)' }}>{lead.priority_score}</p>
            {lead.priority_reason && lead.priority_reason !== 'Standard priority' && (
              <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                <Info className="h-3 w-3" /> {lead.priority_reason}
              </p>
            )}
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Language</p>
            <p className="font-medium mt-0.5" style={{ color: 'var(--text-primary)' }}>{lead.language_preference}</p>
          </div>
          {lead.policy_number && (
            <div className="md:col-span-4">
              <p className="text-sm mb-1" style={{ color: 'var(--text-tertiary)' }}>Policy</p>
              <p className="font-medium mb-2" style={{ color: 'var(--success-text)' }}>{lead.policy_number}</p>
              <PolicyPdfUpload leadId={lead.id} policyNumber={lead.policy_number} />
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        {canInitiateCall && (
          <button className="btn-primary" onClick={handleInitiateCall} disabled={initiating}>
            <Phone className={`h-3.5 w-3.5 ${initiating ? 'animate-pulse' : ''}`} />
            {initiating
              ? 'Initiating…'
              : !smartConnectEnabled && revealedMobile
                ? revealedMobile
                : 'Call Now'}
          </button>
        )}
        {canInitiateCall && !smartConnectEnabled && revealedMobile && (
          <button className="btn-secondary" onClick={handleLogManualCall}>
            <PhoneCall className="h-3.5 w-3.5" />
            Log Call
          </button>
        )}
        {whatsappEnabled && (
          <button className="btn-secondary" onClick={() => setShowWhatsAppModal(true)}>
            <MessageSquare className="h-3.5 w-3.5" /> Send WhatsApp
          </button>
        )}
        {canIssuePolicy && (
          <button className="btn-secondary" onClick={() => setShowPolicyModal(true)}>
            <FileText className="h-3.5 w-3.5" /> Issue Policy
          </button>
        )}
        <button className="btn-secondary" onClick={() => setShowQuoteModal(true)}>
          <Upload className="h-3.5 w-3.5" /> Upload Quote
        </button>
        {canRevealMobile && !revealedMobile && (
          <button className="btn-secondary" onClick={handleRevealMobile} disabled={revealing}>
            <Eye className="h-3.5 w-3.5" />
            {revealing ? 'Revealing…' : 'Reveal Mobile'}
          </button>
        )}
        {revealedMobile && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium"
            style={{ background: 'var(--info-bg)', color: 'var(--info-text)' }}
          >
            <Eye className="h-3.5 w-3.5" />
            {revealedMobile}
          </div>
        )}
        {canSendPaymentLink && ['IN_NEGOTIATION', 'QUOTATION_SENT'].includes(lead.status) && (
          <button className="btn-secondary" onClick={() => setShowPaymentModal(true)}>
            <LinkIcon className="h-3.5 w-3.5" /> Send Payment Link
          </button>
        )}
        {canReassign && (
          <button className="btn-secondary" onClick={openReassignModal}>
            <UserCheck className="h-3.5 w-3.5" /> Reassign
          </button>
        )}
      </div>

      {/* Page-level notification banner */}
      {pageNotif && (
        <div className={pageNotif.type === 'success' ? 'alert-success' : 'alert-danger'}>
          {pageNotif.type === 'success'
            ? <CheckCircle className="h-4 w-4 shrink-0" />
            : <AlertCircle className="h-4 w-4 shrink-0" />
          }
          <span>{pageNotif.message}</span>
          <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setPageNotif(null)}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Notes */}
      {lead.customer_notes && (
        <div className="card">
          <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Notes</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{lead.customer_notes}</p>
        </div>
      )}

      {/* Quotes */}
      {quotes.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <IndianRupee className="h-4 w-4" /> Quotes ({quotes.length})
            </h3>
            {draftQuotes.length > 0 && (
              <button className="btn-primary" onClick={handleSendQuotes} disabled={quoteSending}>
                <Send className="h-3.5 w-3.5" />
                {quoteSending ? 'Sending…' : 'Send to Customer'}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {quotes.map((q) => (
              <div
                key={q.id}
                className="flex items-center justify-between p-3 rounded-md"
                style={{ background: 'var(--surface-subtle)' }}
              >
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    {q.insurer_name || 'Unknown Insurer'}
                  </p>
                  <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                    {q.pdf_filename || 'quote.pdf'}
                    {q.premium_amount != null && ` · ₹${q.premium_amount.toLocaleString()}`}
                    {q.idv != null && ` · IDV ₹${q.idv.toLocaleString()}`}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {q.zero_depreciation && <span className="badge-info">Zero Dep</span>}
                    {q.roadside_assistance && <span className="badge-info">RSA</span>}
                    {q.engine_protection && <span className="badge-info">Engine</span>}
                    {q.consumables_cover && <span className="badge-info">Consumables</span>}
                    {q.ncb_protection && <span className="badge-info">NCB</span>}
                    {q.key_replacement && <span className="badge-info">Key Replace</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0 ml-3">
                  <span className={
                    q.status === 'SELECTED' ? 'badge-success'
                    : q.status === 'SENT' ? 'badge-warning'
                    : q.status === 'REJECTED' ? 'badge-danger'
                    : 'badge-neutral'
                  }>
                    {q.status}
                  </span>
                  {q.status === 'SELECTED' && (
                    <CheckCircle className="h-4 w-4" style={{ color: 'var(--success-text)' }} />
                  )}
                  <button
                    className="btn-icon"
                    title="Download PDF"
                    disabled={downloadingQuote === q.id}
                    onClick={() => handleDownloadQuote(q.id, q.pdf_filename || 'quote.pdf')}
                  >
                    <Download className={`h-3.5 w-3.5 ${downloadingQuote === q.id ? 'animate-pulse' : ''}`} />
                  </button>
                </div>
              </div>
            ))}
            {sentQuotes.length === 0 && draftQuotes.length > 0 && (
              <p className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>
                {draftQuotes.length} quote(s) ready to send. Click "Send to Customer" to share via WhatsApp.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Pending discount approvals */}
      {canActOnDiscount && pendingDiscounts.length > 0 && (
        <div className="space-y-3">
          {pendingDiscounts.map(d => {
            const pct          = d.od_discount_offered ?? 0
            const levelLabel   = d.escalated_to === 'TEAM_LEADER' ? 'Team Leader' : 'Insurance Head'
            // P4: Escalate only when TL is the approver AND discount actually exceeds TL limit
            const showEscalate = currentUser?.role === UserRole.TEAM_LEADER &&
                                 d.escalated_to === 'TEAM_LEADER' &&
                                 pct > discountLimits.team_leader
            // P5: time pending
            const timePending  = d.created_at
              ? (() => {
                  const h = Math.floor((Date.now() - new Date(d.created_at).getTime()) / 3_600_000)
                  const m = Math.round(((Date.now() - new Date(d.created_at).getTime()) / 60_000) % 60)
                  return { label: h > 0 ? `${h}h ${m}m pending` : `${m}m pending`, urgent: h >= 4 }
                })()
              : null

            return (
              <div key={d.id} className="card"
                style={{ borderLeft: '4px solid var(--warning-text)', background: 'var(--warning-bg)' }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Percent className="h-4 w-4 shrink-0" style={{ color: 'var(--warning-text)' }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold" style={{ color: 'var(--warning-text)' }}>
                        {pct}% OD discount — pending {levelLabel} approval
                      </p>
                      {/* P5: escalation chain summary */}
                      {d.escalation_chain && d.escalation_chain.length > 0 && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {d.escalation_chain.map(s =>
                            `${s.role.replace(/_/g, ' ')}${s.comment ? ` — "${s.comment}"` : ''}`
                          ).join(' → ')}
                        </p>
                      )}
                      {d.escalation_comment && !d.escalation_chain?.length && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          Comment: {d.escalation_comment}
                        </p>
                      )}
                      {/* P5: time pending */}
                      {timePending && (
                        <p className="text-xs mt-0.5" style={{ color: timePending.urgent ? 'var(--danger-text)' : 'var(--text-tertiary)' }}>
                          {timePending.label}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <button
                      className="btn-primary"
                      style={{ height: 28, fontSize: 12 }}
                      disabled={discountActing}
                      onClick={() => handleDiscountApprove(d.id)}
                    >
                      <CheckCircle className="h-3.5 w-3.5" /> Approve
                    </button>
                    {/* P4: only show Escalate when discount genuinely exceeds TL limit */}
                    {showEscalate && (
                      <button
                        className="btn-secondary"
                        style={{ height: 28, fontSize: 12 }}
                        onClick={() => { setDiscountAction({ callId: d.id, mode: 'escalate' }); setDiscountComment('') }}
                      >
                        <ChevronUp className="h-3.5 w-3.5" /> Escalate to IH
                      </button>
                    )}
                    <button
                      className="btn-secondary"
                      style={{ height: 28, fontSize: 12, color: 'var(--danger-text)' }}
                      onClick={() => { setDiscountAction({ callId: d.id, mode: 'reject' }); setDiscountComment('') }}
                    >
                      <X className="h-3.5 w-3.5" /> Reject
                    </button>
                  </div>
                </div>
                {/* Inline comment input for reject / escalate */}
                {discountAction?.callId === d.id && (
                  <div className="mt-3 space-y-2">
                    <input
                      type="text"
                      className="input"
                      placeholder={discountAction.mode === 'reject' ? 'Reason for rejection…' : 'Comment for Insurance Head…'}
                      value={discountComment}
                      onChange={e => setDiscountComment(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <button className="btn-primary" style={{ height: 28, fontSize: 12 }}
                        disabled={discountActing} onClick={handleDiscountAction}>
                        Confirm
                      </button>
                      <button className="btn-secondary" style={{ height: 28, fontSize: 12 }}
                        onClick={() => setDiscountAction(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <PreviousPolicyCard leadId={lead.id} />

      {/* Enquiry Tracking Card — consolidated lead history */}
      <div className="card">
        <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Clock className="h-4 w-4" /> Lead History
        </h3>
        <EnquiryTrackingCard leadId={lead.id} />
      </div>

      {/* WhatsApp message history */}
      <MessageHistory leadId={lead.id} />

      {/* Modals */}
      <CallDispositionModal
        isOpen={showCallModal}
        callLogId={activeCallLogId}
        leadId={lead.id}
        source={activeCallSource}
        customerName={lead.customer_name}
        callStatus={activeCallStatus}
        pollTimedOut={pollTimedOut}
        recordingFound={recordingFound}
        onClose={() => { stopPolling(); setShowCallModal(false); setActiveCallLogId(null); setActiveCallStatus(null); setPollTimedOut(false); setRecordingFound(false) }}
        onSaved={handleCallSaved}
      />

      <WhatsAppSendModal
        isOpen={showWhatsAppModal}
        lead={lead}
        onClose={() => setShowWhatsAppModal(false)}
        onSent={() => { setShowWhatsAppModal(false); loadData() }}
      />

      <IssuePolicyModal
        isOpen={showPolicyModal}
        leadId={lead.id}
        customerName={lead.customer_name}
        onClose={() => setShowPolicyModal(false)}
        onIssued={() => { setShowPolicyModal(false); loadData() }}
      />

      {showPaymentModal && (
        <ModalOverlay onClose={() => setShowPaymentModal(false)}>
          <div
            className="modal-panel"
            style={{ maxWidth: '480px' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Send Payment Request — {lead.customer_name}</h2>
              <button
                className="btn-icon"
                onClick={() => setShowPaymentModal(false)}
                disabled={sendingPayment}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="form-hint">
                Customer will see two buttons: <strong>Pay by QR Code</strong> and <strong>Pay via Payment Link</strong>. Both are required.
              </p>
              <div className="form-group">
                <label className="form-label">
                  Amount (₹) <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="number"
                  className="input"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                  placeholder="e.g. 14500"
                  min="1"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  Payment URL <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="url"
                  className="input"
                  value={paymentUrl}
                  onChange={e => setPaymentUrl(e.target.value)}
                  placeholder="https://pay.example.com/invoice/..."
                />
                <p className="form-hint">Sent when customer taps "Pay via Payment Link"</p>
              </div>
              <div className="form-group">
                <label className="form-label">
                  QR Code Image <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="file"
                  className="input"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleQrFileChange}
                  disabled={sendingPayment}
                />
                <p className="form-hint">PNG/JPEG — sent when customer taps "Pay by QR Code"</p>
                {paymentQrPreview && (
                  <img
                    src={paymentQrPreview}
                    alt="QR preview"
                    style={{ marginTop: '8px', maxHeight: '120px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                  />
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  className="btn-secondary"
                  onClick={() => { setShowPaymentModal(false); setPaymentQrFile(null); setPaymentQrPreview(null) }}
                  disabled={sendingPayment}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSendPaymentLink}
                  disabled={!paymentUrl || !paymentAmount || !paymentQrFile || sendingPayment}
                >
                  {sendingPayment ? 'Sending…' : 'Send via WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}

      {showQuoteModal && (
        <QuoteUploadWizard
          leadId={lead.id}
          customerName={lead.customer_name}
          vehicleReg={lead.vehicle_reg_no ?? undefined}
          onClose={() => { setShowQuoteModal(false); loadData() }}
          onQuotesUploaded={loadData}
        />
      )}

      {/* ── Reassign modal ──────────────────────────────────────────────── */}
      {showReassignModal && (
        <ModalOverlay onClose={() => setShowReassignModal(false)}>
          <div className="modal-panel-flex" onClick={e => e.stopPropagation()}>
            <div className="modal-flex-header">
              <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                Reassign Lead
              </h2>
              <button onClick={() => setShowReassignModal(false)}>
                <X className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
              </button>
            </div>
            <div className="modal-flex-body">
              {telecallers.length === 0 ? (
                <div className="empty-state py-4">Loading assignees…</div>
              ) : (
                <div className="space-y-2">
                  {telecallers.map(tc => (
                    <label
                      key={tc.user_id}
                      className="flex items-center justify-between p-3 rounded-lg cursor-pointer"
                      style={{
                        border: `1px solid ${selectedTcId === tc.user_id ? 'var(--brand-primary)' : 'var(--border-default)'}`,
                        background: selectedTcId === tc.user_id ? 'var(--brand-primary-subtle)' : 'var(--surface-default)',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="reassign-tc"
                          value={tc.user_id}
                          checked={selectedTcId === tc.user_id}
                          onChange={() => setSelectedTcId(tc.user_id)}
                        />
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {tc.name}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {tc.language.toUpperCase()} · {tc.open_queue_depth} open leads
                          </p>
                        </div>
                      </div>
                      <span
                        className={tc.open_queue_depth > 20 ? 'badge-danger' : tc.open_queue_depth > 10 ? 'badge-warning' : 'badge-success'}
                      >
                        {tc.open_queue_depth} open
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-flex-footer">
              <button className="btn-secondary" onClick={() => setShowReassignModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleReassign}
                disabled={selectedTcId == null || reassigning}
              >
                {reassigning ? 'Reassigning…' : 'Confirm Reassign'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}
